import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Search,
  Loader2,
  Eye,
  Heart,
  MessageCircle,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Plus,
  Trash2,
  RefreshCw,
  Target,
  BarChart3,
  Clock,
  Flame,
  Settings,
  ChevronRight,
  ArrowUpRight,
  Zap,
  Bell,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API, formatNumber, getPlatformBadgeClass } from "./constants";

// Translations
const TRANSLATIONS = {
  en: {
    title: "Competitor Tracking",
    subtitle: "Monitor competitor content and detect viral trends",
    trendingNow: "Trending Now",
    trendingDesc: "Content with rapid growth detected",
    noTrending: "No trending content detected yet",
    keywordDashboard: "Keyword Dashboard",
    addKeyword: "Add Keyword",
    enterKeyword: "Enter keyword to track",
    cancel: "Cancel",
    add: "Add",
    contentRanking: "Content Ranking",
    sortBy: "Sort by",
    growthRate: "Growth Rate",
    likes: "Likes",
    views: "Views",
    platform: "Platform",
    allPlatforms: "All Platforms",
    refreshAll: "Refresh All",
    refreshing: "Refreshing...",
    settings: "Settings",
    growthThreshold: "Growth Threshold",
    growthThresholdDesc: "Content is marked as 'trending' when growth exceeds this percentage",
    save: "Save",
    lastUpdated: "Last Updated",
    hot: "HOT",
    new: "NEW",
    noContent: "No tracked content for this keyword",
    searchAndSave: "Search & Save",
    totalContent: "Total Content",
    growing: "Growing",
    trending: "Trending",
    deleteKeyword: "Delete Keyword",
    deleteConfirm: "Are you sure you want to delete this keyword and all its tracked content?",
    delete: "Delete",
    detectedAt: "Detected",
    growth: "Growth",
  },
  ko: {
    title: "경쟁사 트래킹",
    subtitle: "경쟁사 콘텐츠 모니터링 및 바이럴 트렌드 감지",
    trendingNow: "🔥 급상승 콘텐츠",
    trendingDesc: "급격한 성장이 감지된 콘텐츠",
    noTrending: "아직 급상승 콘텐츠가 없습니다",
    keywordDashboard: "키워드 대시보드",
    addKeyword: "키워드 추가",
    enterKeyword: "추적할 키워드 입력",
    cancel: "취소",
    add: "추가",
    contentRanking: "콘텐츠 랭킹",
    sortBy: "정렬",
    growthRate: "성장률",
    likes: "좋아요",
    views: "조회수",
    platform: "플랫폼",
    allPlatforms: "전체 플랫폼",
    refreshAll: "전체 새로고침",
    refreshing: "새로고침 중...",
    settings: "설정",
    growthThreshold: "급상승 기준",
    growthThresholdDesc: "이 비율을 초과하면 '급상승' 콘텐츠로 표시됩니다",
    save: "저장",
    lastUpdated: "마지막 업데이트",
    hot: "HOT",
    new: "NEW",
    noContent: "이 키워드에 대한 추적 콘텐츠가 없습니다",
    searchAndSave: "검색 & 저장",
    totalContent: "전체 콘텐츠",
    growing: "성장 중",
    trending: "급상승",
    deleteKeyword: "키워드 삭제",
    deleteConfirm: "이 키워드와 모든 추적 콘텐츠를 삭제하시겠습니까?",
    delete: "삭제",
    detectedAt: "감지 시간",
    growth: "성장",
  },
  zh: {
    title: "竞品追踪",
    subtitle: "监控竞品内容并检测病毒式趋势",
    trendingNow: "🔥 热门趋势",
    trendingDesc: "检测到快速增长的内容",
    noTrending: "尚未检测到热门内容",
    keywordDashboard: "关键词仪表板",
    addKeyword: "添加关键词",
    enterKeyword: "输入要追踪的关键词",
    cancel: "取消",
    add: "添加",
    contentRanking: "内容排名",
    sortBy: "排序",
    growthRate: "增长率",
    likes: "点赞",
    views: "浏览量",
    platform: "平台",
    allPlatforms: "全部平台",
    refreshAll: "全部刷新",
    refreshing: "刷新中...",
    settings: "设置",
    growthThreshold: "热门阈值",
    growthThresholdDesc: "当增长超过此百分比时，内容将被标记为'热门'",
    save: "保存",
    lastUpdated: "最后更新",
    hot: "热门",
    new: "新",
    noContent: "该关键词没有追踪内容",
    searchAndSave: "搜索并保存",
    totalContent: "总内容",
    growing: "增长中",
    trending: "热门",
    deleteKeyword: "删除关键词",
    deleteConfirm: "确定要删除此关键词及其所有追踪内容吗？",
    delete: "删除",
    detectedAt: "检测时间",
    growth: "增长",
  },
};

// Platform colors
const PLATFORM_COLORS = {
  youtube: "#FF0000",
  tiktok: "#69C9D0",
  douyin: "#25F4EE",
  xiaohongshu: "#FF2442",
  bilibili: "#00A1D6",
  weibo: "#E6162D",
  instagram: "#E4405F",
};

// Trending content card
const TrendingCard = ({ content, t }) => {
  const growth = content.max_growth_rate || 0;
  
  return (
    <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
            <Flame className="h-3 w-3 mr-0.5" />
            {t.hot}
          </Badge>
          <Badge variant="outline" className={getPlatformBadgeClass(content.platform)}>
            {content.platform}
          </Badge>
          <span className="text-xs text-muted-foreground">#{content.keyword}</span>
        </div>
        <p className="text-sm font-medium truncate">{content.title}</p>
        <div className="flex items-center gap-3 mt-1.5 text-xs">
          <span className="flex items-center gap-1 text-green-500 font-medium">
            <ArrowUpRight className="h-3 w-3" />
            +{growth.toFixed(0)}%
          </span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <Heart className="h-3 w-3" />
            {formatNumber(content.initial_likes)} → {formatNumber(content.latest_likes)}
          </span>
          {content.latest_views > 0 && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Eye className="h-3 w-3" />
              {formatNumber(content.latest_views)}
            </span>
          )}
        </div>
      </div>
      <a
        href={content.post_url}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 hover:bg-accent rounded"
      >
        <ExternalLink className="h-4 w-4 text-muted-foreground" />
      </a>
    </div>
  );
};

// Keyword card
const KeywordCard = ({ keyword, isSelected, onClick, t }) => {
  const trendingCount = keyword.trending_count || 0;
  const totalContent = keyword.total_content || 0;
  
  return (
    <button
      onClick={() => onClick(keyword.keyword)}
      className={`p-3 rounded-lg border text-left transition-all ${
        isSelected
          ? "border-primary bg-primary/10"
          : "border-border hover:border-primary/50 hover:bg-accent/50"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-sm">#{keyword.keyword}</span>
        {trendingCount > 0 && (
          <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
            {trendingCount} {t.hot}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{totalContent} {t.totalContent}</span>
        {keyword.growing_count > 0 && (
          <span className="text-green-500">↑{keyword.growing_count}</span>
        )}
      </div>
    </button>
  );
};

// Mobile Content Card
const MobileContentCard = ({ content, t }) => {
  const likesGrowth = content.likes_growth_rate || 0;
  const viewsGrowth = content.views_growth_rate || 0;
  const isTrending = content.is_trending;

  return (
    <div className={`p-3 rounded-lg border transition-colors space-y-2 ${isTrending ? "border-orange-500/30 bg-orange-500/5" : "border-border/30"}`} data-testid={`content-card-${content.content_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">
            {isTrending && <Flame className="h-3 w-3 inline mr-1 text-orange-500" />}
            {content.title}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className={`${getPlatformBadgeClass(content.platform)} text-xs`}>
              {content.platform}
            </Badge>
            {content.author && <span className="text-xs text-muted-foreground truncate">{content.author}</span>}
          </div>
        </div>
        {content.post_url && (
          <a href={content.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted text-primary shrink-0">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.likes}</span>
          <span className={`font-mono font-medium ${likesGrowth > 0 ? "text-green-500" : likesGrowth < 0 ? "text-red-500" : ""}`}>
            {likesGrowth > 0 ? "+" : ""}{likesGrowth.toFixed(1)}%
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t.views}</span>
          <span className={`font-mono font-medium ${viewsGrowth > 0 ? "text-green-500" : viewsGrowth < 0 ? "text-red-500" : ""}`}>
            {formatNumber(content.latest_views)}
          </span>
        </div>
        <div className="flex items-center justify-between col-span-2">
          <span className="text-muted-foreground">{t.likes}</span>
          <span className="font-mono text-muted-foreground">
            {formatNumber(content.initial_likes)} → {formatNumber(content.latest_likes)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Content row (desktop table)
const ContentRow = ({ content, t }) => {
  const likesGrowth = content.likes_growth_rate || 0;
  const viewsGrowth = content.views_growth_rate || 0;
  const isTrending = content.is_trending;
  
  return (
    <TableRow className={isTrending ? "bg-orange-500/5" : ""}>
      <TableCell>
        <div className="flex items-center gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate max-w-[200px]">
              {isTrending && <Flame className="h-3 w-3 inline mr-1 text-orange-500" />}
              {content.title}
            </p>
            <p className="text-xs text-muted-foreground">{content.author}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={getPlatformBadgeClass(content.platform)}>
          {content.platform}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end">
          <span className={`text-sm font-medium ${likesGrowth > 0 ? "text-green-500" : likesGrowth < 0 ? "text-red-500" : ""}`}>
            {likesGrowth > 0 ? "+" : ""}{likesGrowth.toFixed(1)}%
          </span>
          <span className="text-xs text-muted-foreground">
            {formatNumber(content.initial_likes)} → {formatNumber(content.latest_likes)}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end">
          <span className={`text-sm font-medium ${viewsGrowth > 0 ? "text-green-500" : viewsGrowth < 0 ? "text-red-500" : ""}`}>
            {viewsGrowth > 0 ? "+" : ""}{viewsGrowth.toFixed(1)}%
          </span>
          <span className="text-xs text-muted-foreground">
            {formatNumber(content.latest_views)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <a
          href={content.post_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 hover:bg-accent rounded inline-flex"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </a>
      </TableCell>
    </TableRow>
  );
};

const CompetitorTrackingPage = ({ lang = "ko" }) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.ko;

  // State
  const [keywords, setKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [trendingContent, setTrendingContent] = useState([]);
  const [keywordContent, setKeywordContent] = useState([]);
  const [settings, setSettings] = useState({ growth_threshold: 100 });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchingKeyword, setSearchingKeyword] = useState(null);
  
  // Dialogs
  const [addKeywordOpen, setAddKeywordOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);
  
  // Form state
  const [newKeyword, setNewKeyword] = useState("");
  const [tempThreshold, setTempThreshold] = useState(100);
  const [sortBy, setSortBy] = useState("growth_rate");
  const [platformFilter, setPlatformFilter] = useState("all");

  // Fetch keywords with stats
  const fetchKeywords = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracking/keywords-with-stats`);
      setKeywords(response.data.keywords || []);
      if (response.data.growth_threshold) {
        setSettings((prev) => ({ ...prev, growth_threshold: response.data.growth_threshold }));
        setTempThreshold(response.data.growth_threshold);
      }
    } catch (error) {
      console.error("Failed to fetch keywords:", error);
    }
  }, []);

  // Fetch trending content
  const fetchTrending = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracking/trending`, {
        params: { limit: 10 }
      });
      setTrendingContent(response.data.trending_content || []);
    } catch (error) {
      console.error("Failed to fetch trending:", error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tracking/settings`);
      // Merge with defaults so no field is lost
      setSettings((prev) => ({ ...prev, ...response.data }));
      setTempThreshold(response.data.growth_threshold || 100);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  }, []);

  // Fetch content for selected keyword
  const fetchKeywordContent = useCallback(async (keyword) => {
    if (!keyword) return;
    
    setLoading(true);
    try {
      const params = {
        sort_by: sortBy,
        limit: 100
      };
      if (platformFilter !== "all") {
        params.platform = platformFilter;
      }
      
      const response = await axios.get(`${API}/tracking/content/${encodeURIComponent(keyword)}`, { params });
      
      // Mark trending items
      const threshold = settings.growth_threshold || 100;
      const contents = (response.data.contents || []).map(c => ({
        ...c,
        is_trending: Math.max(c.likes_growth_rate || 0, c.views_growth_rate || 0) >= threshold
      }));
      
      setKeywordContent(contents);
    } catch (error) {
      console.error("Failed to fetch keyword content:", error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, platformFilter, settings.growth_threshold]);

  // Initial load
  useEffect(() => {
    fetchKeywords();
    fetchTrending();
    fetchSettings();
  }, [fetchKeywords, fetchTrending, fetchSettings]);

  // Load content when keyword selected
  useEffect(() => {
    if (selectedKeyword) {
      fetchKeywordContent(selectedKeyword);
    }
  }, [selectedKeyword, fetchKeywordContent]);

  // Add keyword
  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;
    
    try {
      await axios.post(`${API}/tracking/keywords`, {
        keyword: newKeyword.trim(),
        platforms: ["youtube", "tiktok", "douyin", "xiaohongshu", "bilibili", "weibo", "instagram"]
      });
      toast.success(`키워드 "${newKeyword}" 추가됨`);
      setNewKeyword("");
      setAddKeywordOpen(false);
      fetchKeywords();
    } catch (error) {
      toast.error("키워드 추가 실패");
    }
  };

  // Delete keyword
  const handleDeleteKeyword = async () => {
    if (!keywordToDelete) return;
    
    try {
      await axios.delete(`${API}/tracking/keywords/${encodeURIComponent(keywordToDelete)}`);
      toast.success(`키워드 "${keywordToDelete}" 삭제됨`);
      setDeleteDialogOpen(false);
      setKeywordToDelete(null);
      if (selectedKeyword === keywordToDelete) {
        setSelectedKeyword(null);
        setKeywordContent([]);
      }
      fetchKeywords();
      fetchTrending();
    } catch (error) {
      toast.error("키워드 삭제 실패");
    }
  };

  // Search and save for keyword
  const handleSearchAndSave = async (keyword) => {
    setSearchingKeyword(keyword);
    try {
      await axios.post(`${API}/tracking/search-and-save`, {
        keyword,
        platforms: ["youtube", "tiktok", "douyin", "xiaohongshu", "bilibili", "weibo", "instagram"]
      });
      toast.success(`"${keyword}" 검색 완료`);
      fetchKeywords();
      fetchTrending();
      if (selectedKeyword === keyword) {
        fetchKeywordContent(keyword);
      }
    } catch (error) {
      toast.error("검색 실패");
    } finally {
      setSearchingKeyword(null);
    }
  };

  // Refresh all keywords with polling
  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/tracking/auto-refresh`);
      toast.success(t.refreshing || "새로고침 시작됨...");
      
      // Poll for completion every 5 seconds, max 2 minutes
      let attempts = 0;
      const maxAttempts = 24;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/tracking/refresh-status`);
          const status = statusRes.data.refresh_status;
          
          if (status === "completed" || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setRefreshing(false);
            fetchKeywords();
            fetchTrending();
            if (selectedKeyword) {
              fetchKeywordContent(selectedKeyword);
            }
            if (status === "completed") {
              toast.success("새로고침 완료");
            }
          }
        } catch {
          // Ignore polling errors
        }
      }, 5000);
    } catch (error) {
      toast.error("새로고침 실패");
      setRefreshing(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/tracking/settings`, {
        growth_threshold: tempThreshold,
        time_window_hours: 24,
        auto_search_interval_hours: 24
      });
      setSettings((prev) => ({ ...prev, growth_threshold: tempThreshold }));
      toast.success("설정 저장됨");
      setSettingsOpen(false);
      fetchTrending();
      if (selectedKeyword) {
        fetchKeywordContent(selectedKeyword);
      }
    } catch (error) {
      toast.error("설정 저장 실패");
    }
  };

  // Total trending badge count
  const totalTrending = trendingContent.length;

  return (
    <div className="space-y-6" data-testid="competitor-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6 text-primary" />
            {t.title}
            {totalTrending > 0 && (
              <Badge className="bg-red-500 text-white ml-2">
                <Flame className="h-3 w-3 mr-1" />
                {totalTrending}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground text-sm">{t.subtitle}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {refreshing ? t.refreshing : t.refreshAll}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Trending Now Section */}
      <Card className="border-orange-500/30 bg-gradient-to-r from-orange-500/5 to-red-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-orange-500">
            <Flame className="h-5 w-5" />
            {t.trendingNow}
            {totalTrending > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalTrending}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>{t.trendingDesc} (≥{settings.growth_threshold}%)</CardDescription>
        </CardHeader>
        <CardContent>
          {trendingContent.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {trendingContent.slice(0, 6).map((content, idx) => (
                <TrendingCard key={content.content_id || idx} content={content} t={t} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Zap className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>{t.noTrending}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Keyword Dashboard */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {t.keywordDashboard}
            </CardTitle>
            <Dialog open={addKeywordOpen} onOpenChange={setAddKeywordOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t.addKeyword}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t.addKeyword}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input
                    placeholder={t.enterKeyword}
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddKeyword()}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddKeywordOpen(false)}>
                    {t.cancel}
                  </Button>
                  <Button onClick={handleAddKeyword}>{t.add}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {keywords.length > 0 ? (
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {keywords.map((kw) => (
                <KeywordCard
                  key={kw.keyword}
                  keyword={kw}
                  isSelected={selectedKeyword === kw.keyword}
                  onClick={setSelectedKeyword}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>키워드를 추가하여 경쟁사 콘텐츠를 추적하세요</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Ranking for Selected Keyword */}
      {selectedKeyword && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  #{selectedKeyword}
                  <ChevronRight className="h-4 w-4" />
                  {t.contentRanking}
                </CardTitle>
                <CardDescription>
                  {keywordContent.length} {t.totalContent}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[110px] sm:w-[140px] h-9 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="growth_rate">{t.growthRate}</SelectItem>
                    <SelectItem value="latest_likes">{t.likes}</SelectItem>
                    <SelectItem value="latest_views">{t.views}</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                  <SelectTrigger className="w-[110px] sm:w-[140px] h-9 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.allPlatforms}</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="douyin">Douyin</SelectItem>
                    <SelectItem value="xiaohongshu">Xiaohongshu</SelectItem>
                    <SelectItem value="bilibili">Bilibili</SelectItem>
                    <SelectItem value="weibo">Weibo</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearchAndSave(selectedKeyword)}
                  disabled={searchingKeyword === selectedKeyword}
                >
                  {searchingKeyword === selectedKeyword ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Search className="h-4 w-4 mr-1" />
                  )}
                  {t.searchAndSave}
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    setKeywordToDelete(selectedKeyword);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : keywordContent.length > 0 ? (
              <>
                {/* Mobile: Card Layout */}
                <div className="md:hidden">
                  <div className="h-[500px] overflow-y-auto space-y-2 pr-1">
                    {keywordContent.map((content, idx) => (
                      <MobileContentCard key={content.content_id || idx} content={content} t={t} />
                    ))}
                  </div>
                </div>

                {/* Desktop: Table Layout */}
                <div className="hidden md:block">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[250px]">Content</TableHead>
                          <TableHead>{t.platform}</TableHead>
                          <TableHead className="text-right">{t.likes} {t.growth}</TableHead>
                          <TableHead className="text-right">{t.views}</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {keywordContent.map((content, idx) => (
                          <ContentRow key={content.content_id || idx} content={content} t={t} />
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              </>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <Search className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>{t.noContent}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => handleSearchAndSave(selectedKeyword)}
                >
                  <Search className="h-4 w-4 mr-1" />
                  {t.searchAndSave}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t.settings}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t.growthThreshold}</Label>
                <span className="text-sm font-medium">{tempThreshold}%</span>
              </div>
              <Slider
                value={[tempThreshold]}
                onValueChange={([v]) => setTempThreshold(v)}
                min={10}
                max={500}
                step={10}
              />
              <p className="text-xs text-muted-foreground">{t.growthThresholdDesc}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleSaveSettings}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deleteKeyword}</DialogTitle>
            <DialogDescription>{t.deleteConfirm}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDeleteKeyword}>
              {t.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompetitorTrackingPage;
